import { GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid-pro'
import BaseModal from 'ext-ui/components/BaseModal/BaseModal'
import Button from 'ext-ui/components/Button/Button'
import TextInput from 'ext-ui/components/TextInput/TextInput'
import SavedViewsIcon from 'ext-ui/svg/icon/SavedViewsIcon'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { CardholderSelection } from 'src/app/CardFeedDetails/components/AssignCardholderDropdown'
import * as style from 'src/app/CardFeedDetails/components/CardFeedCardsTable.style'
import { buildCardFeedColumns } from 'src/app/CardFeedDetails/components/cardFeedColumns'
import CardFeedFailedEmptyState from 'src/app/CardFeedDetails/components/CardFeedFailedEmptyState'
import CardFeedPendingEmptyState from 'src/app/CardFeedDetails/components/CardFeedPendingEmptyState'
import CardSelectionSnackbar from 'src/app/CardFeedDetails/components/CardSelectionSnackbar'
import CardStatusFilter from 'src/app/CardFeedDetails/components/CardStatusFilter'
import { resolveSelectedCards } from 'src/app/CardFeedDetails/utils/resolveSelectedCards'
import {
  CardFeedCardsView,
  CardFeedSortField,
  selectVisibleCards,
} from 'src/app/CardFeedDetails/utils/selectVisibleCards'
import Box from 'src/components/Box/Box'
import DataGrid, { DataGridRowHeight } from 'src/components/DataGrid/DataGrid'
import UtilityToolbarDropdown from 'src/components/DataGrid/UtilityToolbar/UtilityToolbarDropdown'
import SavedViewsDropdown from 'src/components/SavedViewsDropdown/SavedViewsDropdown'
import { useTranslate } from 'src/localization/Trns'
import {
  CardFeedCard,
  CardFeedCardStatusFilter,
} from 'src/queries/cardFeeds/cardFeeds.models'
import useQueryCardFeedCards from 'src/queries/cardFeeds/domain/useQueryCardFeedCards'
import {
  CardFeedSavedViewModel,
  cardFeedViewToFilters,
  filtersToCardFeedView,
  useCreateCardFeedSavedView,
  useDeleteCardFeedSavedView,
  useFetchCardFeedSavedViews,
} from 'src/queries/cardFeedSavedViews.queries'
import { parseBackendErrors } from 'src/utils/error.utils'

export interface CardFeedCardsTableProps {
  feedId: string
  /**
   * Called with the cards the user wants to assign an account owner to —
   * fires when the user clicks the snackbar's primary action with one or more
   * unassigned cards selected. Required because the snackbar's primary action
   * is part of the table's bulk-action contract; a hypothetical read-only
   * caller would need to also suppress the snackbar, not just omit the
   * callback.
   */
  onAssignRequest: (cards: CardFeedCard[]) => void
  /**
   * Fires when the user picks a cardholder from a single row's inline
   * dropdown — bypasses the bulk flyout and goes straight to the parent's
   * confirmation modal with both the card and the selected recipient.
   */
  onAssignFromRow: (card: CardFeedCard, selection: CardholderSelection) => void
  /** Fires when the user picks Reinvite from a PENDING row's action menu. */
  onReinvite: (card: CardFeedCard) => void
  /** Fires when the user picks Unassign from a PENDING row's action menu. */
  onUnassign: (card: CardFeedCard) => void
  /**
   * Incremented by the parent after a successful assignment to clear the
   * current selection — the assigned cards transition out of UNASSIGNED, so
   * the bulk selection no longer applies.
   */
  resetSelectionSignal?: number
  /**
   * The feed is connected (ACTIVE). Only an active feed can take assignments,
   * so when false the grid is read-only: row checkboxes and the row-level
   * assign dropdown are suppressed.
   */
  isActive?: boolean
  /**
   * The feed is SUBMITTED (pending) — the bank hasn't activated it yet, so it
   * has no cards and nothing to search or filter. Replaces the entire table
   * (toolbar and grid included) with the illustrated "connecting with your
   * bank" empty state.
   */
  isPending?: boolean
  /**
   * The feed is FAILED — it never connected, so there are no cards. Replaces
   * the toolbar and grid with the illustrated "error connecting" empty state
   * and skips the cards query.
   */
  isFailed?: boolean
}

const GRID_HEIGHT = 560
const SORTABLE_FIELDS: CardFeedSortField[] = [
  'cardName',
  'cardHolder',
  'status',
  'dateAdded',
]

// MUI X 8 models row selection as `{ type, ids }`, not a plain id array. A
// factory (not a shared constant) guarantees every init/reset gets a fresh
// model with its own Set, so no instance or reset can mutate another's state.
const createEmptySelection = (): GridRowSelectionModel => ({
  type: 'include',
  ids: new Set(),
})

const DEFAULT_SORT_MODEL: GridSortModel = [{ field: 'status', sort: 'asc' }]

const statusArraysEqual = (
  a: CardFeedCardStatusFilter[],
  b: CardFeedCardStatusFilter[],
): boolean => {
  if (a.length !== b.length) return false
  const setA = new Set(a)
  return b.every((s) => setA.has(s))
}

const CardFeedCardsTable: React.FC<CardFeedCardsTableProps> = ({
  feedId,
  onAssignRequest,
  onAssignFromRow,
  onReinvite,
  onUnassign,
  resetSelectionSignal,
  isActive = false,
  isPending,
  isFailed,
}) => {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  // Pending and failed feeds have no cards — skip the request and show the
  // appropriate empty state instead.
  const { cards, isTruncated, isLoading, isError } = useQueryCardFeedCards(
    feedId,
    !isPending && !isFailed,
  )

  const [searchValue, setSearchValue] = useState('')
  const [statuses, setStatuses] = useState<CardFeedCardStatusFilter[]>([])
  // Default to status ascending so UNASSIGNED (actionable) cards surface at
  // the top, with card name as the tie-break within each group (EX-39728).
  const [sortModel, setSortModel] = useState<GridSortModel>(DEFAULT_SORT_MODEL)
  const [selectionModel, setSelectionModel] =
    useState<GridRowSelectionModel>(createEmptySelection)

  // Saved views state
  const [selectedViewId, setSelectedViewId] = useState<string | undefined>(
    undefined,
  )
  const [isSaveViewOpen, setIsSaveViewOpen] = useState(false)
  const [viewNameInput, setViewNameInput] = useState('')
  const [deletingViewId, setDeletingViewId] = useState<string | undefined>(
    undefined,
  )
  // Captured at delete-confirm open so the modal keeps showing the name during
  // its close animation even after deletingViewId is cleared on close.
  const [deletingViewName, setDeletingViewName] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  // After a successful create, the freshly created view isn't in the fetched
  // list until the invalidated query refetches. Hold it locally so the
  // selected-view lookups (name, change detection) resolve immediately rather
  // than briefly falling back to "Unsaved View" / the system-default baseline.
  const [optimisticView, setOptimisticView] = useState<
    CardFeedSavedViewModel | undefined
  >(undefined)

  const { data: savedViewsData } = useFetchCardFeedSavedViews()
  const {
    mutate: createView,
    isPending: isCreatingView,
    error: createViewError,
    reset: resetCreateView,
  } = useCreateCardFeedSavedView()
  const {
    mutate: deleteView,
    isPending: isDeletingView,
    error: deleteViewError,
    reset: resetDeleteView,
  } = useDeleteCardFeedSavedView()

  const fetchedViews = savedViewsData?.userSavedViews ?? []
  // Surface the optimistic snapshot until the refetch catches up, then drop it
  // (the effect below clears it) so a later delete can't resurrect it.
  const savedViews = useMemo(
    () =>
      optimisticView && !fetchedViews.some((v) => v.id === optimisticView.id)
        ? [...fetchedViews, optimisticView]
        : fetchedViews,
    [fetchedViews, optimisticView],
  )

  useEffect(() => {
    if (
      optimisticView &&
      fetchedViews.some((v) => v.id === optimisticView.id)
    ) {
      setOptimisticView(undefined)
    }
  }, [fetchedViews, optimisticView])

  useEffect(() => setSelectionModel(createEmptySelection()), [feedId])
  useEffect(() => {
    if (resetSelectionSignal === undefined) return
    setSelectionModel(createEmptySelection())
  }, [resetSelectionSignal])

  const view: CardFeedCardsView = useMemo(() => {
    const sort = sortModel[0]
    const sortField =
      sort && SORTABLE_FIELDS.includes(sort.field as CardFeedSortField)
        ? (sort.field as CardFeedSortField)
        : undefined
    return {
      search: searchValue,
      statuses,
      sortField,
      sortDirection: sort?.sort === 'desc' ? 'DESC' : 'ASC',
    }
  }, [searchValue, statuses, sortModel])

  const selectedView = useMemo(
    () =>
      selectedViewId
        ? savedViews.find((v) => v.id === selectedViewId)
        : undefined,
    [selectedViewId, savedViews],
  )

  const hasViewChange = useMemo(() => {
    const baseline = selectedView
      ? filtersToCardFeedView(selectedView.filters)
      : {
          search: '',
          statuses: [] as CardFeedCardStatusFilter[],
          sortField: 'status',
          sortDirection: 'ASC' as const,
        }
    return (
      (view.search || '') !== (baseline.search || '') ||
      !statusArraysEqual(view.statuses ?? [], baseline.statuses ?? []) ||
      (view.sortField ?? 'status') !== (baseline.sortField ?? 'status') ||
      (view.sortDirection ?? 'ASC') !== (baseline.sortDirection ?? 'ASC')
    )
  }, [view, selectedView])

  const selectedViewDisplayName = useMemo(() => {
    // Any divergence from the applied view's baseline (filters OR sort) is an
    // unsaved edit, regardless of whether a saved view or the system default is
    // active. hasViewChange compares against the applied view, so this also
    // catches sorting back to the system-default sort while a saved view is on.
    if (hasViewChange) return translate('Unsaved View')
    if (selectedViewId === undefined) return translate('Default View')
    const name = savedViews.find((v) => v.id === selectedViewId)?.displayName
    return name ?? translate('Unsaved View')
  }, [selectedViewId, savedViews, hasViewChange, translate])

  const visibleCards = useMemo(
    () => selectVisibleCards(cards, view),
    [cards, view],
  )

  const columns = useMemo(
    () =>
      buildCardFeedColumns({
        translate,
        onAssignFromRow,
        onReinvite,
        onUnassign,
        isActive,
      }),
    [translate, onAssignFromRow, onReinvite, onUnassign, isActive],
  )

  const isFiltering = searchValue.trim().length > 0 || statuses.length > 0

  // Resolve against visibleCards (the rows in the grid), not the full feed, so
  // "select all" while a filter is active never pulls in cards the user can't
  // see. See resolveSelectedCards for the include/exclude model handling.
  const selectedCards = useMemo(
    () => resolveSelectedCards(visibleCards, selectionModel),
    [visibleCards, selectionModel],
  )

  const handleAssignSelected = (): void => {
    if (selectedCards.length === 0) return
    onAssignRequest(selectedCards)
  }

  const handleSortModelChange = (newModel: GridSortModel): void => {
    setSortModel(newModel)
    const sort = newModel[0]
    if (sort) {
      analytics.track('card feed details', {
        action: 'sort applied',
        sort_by: sort.field,
        sort_direction: sort.sort === 'desc' ? 'desc' : 'asc',
      })
    }
  }

  const handleRowSelectionModelChange = (
    newModel: GridRowSelectionModel,
  ): void => {
    // Compare resolved selectable counts rather than trusting the model type.
    // MUI keeps `type: 'exclude'` after a header select-all, so a subsequent row
    // *deselect* arrives as an exclude model with a growing excluded id set —
    // treating every exclude model as "select all" would emit a bogus
    // `card selected` event for deselects and other shrink operations. Firing
    // only when the resolved count grows avoids that.
    const prevCount = selectedCards.length
    const newCount = resolveSelectedCards(visibleCards, newModel).length
    setSelectionModel(newModel)

    if (newCount <= prevCount) return

    // Distinguish the header "select all" from a single row toggle by how many
    // rows were added at once. MUI represents select-all as either an empty
    // `exclude` model (paginated / server data) or an `include` model holding
    // every id (the in-memory case) — a bulk add either way, vs. a single row
    // checkbox that adds exactly one.
    const isSelectAll =
      (newModel.type === 'exclude' && newModel.ids.size === 0) ||
      newCount - prevCount > 1
    analytics.track('card feed details', {
      action: 'card selected',
      selection: isSelectAll ? 'all' : 'single',
    })
  }

  const handleSetView = useCallback(
    (viewId: string): void => {
      const v = savedViews.find((sv) => sv.id === viewId)
      if (!v) return
      const cf = filtersToCardFeedView(v.filters)
      setSearchValue(cf.search ?? '')
      setStatuses(cf.statuses ?? [])
      // Guard against an unknown sortField from the backend: a sort model that
      // references a non-existent column makes MUI/X behave unexpectedly, so
      // fall back to the default sort field when it isn't a known column.
      const sortField =
        cf.sortField &&
        SORTABLE_FIELDS.includes(cf.sortField as CardFeedSortField)
          ? cf.sortField
          : 'status'
      const sortDir = cf.sortDirection === 'DESC' ? 'desc' : ('asc' as const)
      setSortModel([{ field: sortField, sort: sortDir }])
      setSelectedViewId(viewId)
      analytics.track('card feed details', { action: 'saved view applied' })
    },
    [savedViews, analytics],
  )

  const handleResetToDefault = useCallback((): void => {
    setSearchValue('')
    setStatuses([])
    setSortModel(DEFAULT_SORT_MODEL)
    setSelectedViewId(undefined)
    analytics.track('card feed details', { action: 'reset to default view' })
  }, [analytics])

  const handleOpenSaveView = useCallback((): void => {
    setViewNameInput('')
    // Clear any error from a prior failed attempt so reopening the form starts
    // clean rather than showing a stale create error.
    resetCreateView()
    setIsSaveViewOpen(true)
  }, [resetCreateView])

  const handleCancelSaveView = useCallback((): void => {
    setIsSaveViewOpen(false)
    setViewNameInput('')
  }, [])

  const handleConfirmSaveView = useCallback((): void => {
    const name = viewNameInput.trim()
    if (!name) return
    const filters = cardFeedViewToFilters(view)
    createView(
      { displayName: name, filters },
      {
        onSuccess: (created) => {
          // Seed the snapshot from what we just saved so the selected view
          // resolves immediately, before the list query refetches.
          setOptimisticView({
            id: created.id,
            viewType: 'CARD_FEED',
            displayName: name,
            filters,
          })
          setSelectedViewId(created.id)
          setIsSaveViewOpen(false)
          setViewNameInput('')
          analytics.track('card feed details', { action: 'saved view created' })
        },
      },
    )
  }, [viewNameInput, view, createView, analytics])

  const handleDeleteView = useCallback(
    (viewId: string): void => {
      setDeletingViewId(viewId)
      setDeletingViewName(
        savedViews.find((v) => v.id === viewId)?.displayName ?? '',
      )
      // Clear any error from a prior failed delete so the modal opens clean.
      resetDeleteView()
      setShowDeleteConfirm(true)
    },
    [savedViews, resetDeleteView],
  )

  const handleCloseDeleteConfirm = useCallback((): void => {
    // Don't allow closing mid-delete so the mutation can settle.
    if (isDeletingView) return
    setShowDeleteConfirm(false)
    // Drop the stale target id (the name is retained for the close animation).
    setDeletingViewId(undefined)
    resetDeleteView()
  }, [isDeletingView, resetDeleteView])

  const handleConfirmDeleteView = useCallback((): void => {
    if (!deletingViewId) return
    deleteView(
      { body: { savedViewId: deletingViewId } },
      {
        onSuccess: () => {
          if (selectedViewId === deletingViewId) setSelectedViewId(undefined)
          setShowDeleteConfirm(false)
          setDeletingViewId(undefined)
          analytics.track('card feed details', { action: 'saved view deleted' })
        },
      },
    )
  }, [deletingViewId, selectedViewId, deleteView, analytics])

  const noRowsOverlay = (): React.ReactElement => (
    <div
      css={style.overlay}
      data-testid={
        isFiltering ? 'card-feed-cards-no-match' : 'card-feed-cards-empty'
      }
    >
      {isFiltering
        ? translate('No cards match your search or filters.')
        : translate(
            'No cards yet — your bank will activate this feed shortly.',
          )}
    </div>
  )

  // A pending feed has no cards and nothing to search or filter yet, so drop
  // the toolbar and grid entirely and show only the "connecting with your bank"
  // empty state.
  if (isPending) {
    return (
      <Box
        noPadding
        outlineBorder
        css={style.tableCard}
        data-testid="card-feed-cards-table"
      >
        <CardFeedPendingEmptyState />
      </Box>
    )
  }

  // A failed feed never connected and has no cards, so drop the toolbar and
  // grid and show only the "error connecting" empty state.
  if (isFailed) {
    return (
      <Box
        noPadding
        outlineBorder
        css={style.tableCard}
        data-testid="card-feed-cards-table"
      >
        <CardFeedFailedEmptyState />
      </Box>
    )
  }

  return (
    <Box
      noPadding
      outlineBorder
      css={style.tableCard}
      data-testid="card-feed-cards-table"
    >
      <div css={style.toolbar}>
        <UtilityToolbarDropdown
          buttonText={selectedViewDisplayName}
          dropdown={
            <SavedViewsDropdown
              handleSetView={handleSetView}
              handleResetToDefault={handleResetToDefault}
              handleSaveNewView={handleOpenSaveView}
              handleDeleteView={handleDeleteView}
              views={savedViews}
              selectedViewId={selectedViewId}
              hasViewChange={hasViewChange}
            />
          }
          image={<SavedViewsIcon />}
          testId="cardFeedSavedViewsDropdown"
          selected={hasViewChange}
          alignment="left"
        />
        <div css={style.search}>
          <TextInput
            value={searchValue}
            onChange={(e): void => setSearchValue(e.target.value)}
            placeholder={translate('Search...')}
            aria-label={translate('Search cards')}
            testId="card-feed-cards-search"
          />
        </div>
        <CardStatusFilter statuses={statuses} onChange={setStatuses} />
      </div>

      {isSaveViewOpen && (
        <div
          className="flex-row gap-18 justify-between align-end"
          css={style.saveViewForm}
        >
          <TextInput
            label={translate('View Name')}
            placeholder={translate('Name this view...')}
            testId="cardFeedViewNameInput"
            requirement="required"
            className="flex-1"
            value={viewNameInput}
            onChange={(e): void => setViewNameInput(e.target.value)}
            errorText={
              createViewError?.response?.data
                ? parseBackendErrors(createViewError.response.data)
                    .topLevelError
                : undefined
            }
          />
          <div className="flex-row gap-6">
            <Button variant="secondary" onClick={handleCancelSaveView}>
              {translate('Cancel')}
            </Button>
            <Button
              disabled={isCreatingView || !viewNameInput.trim()}
              onClick={handleConfirmSaveView}
            >
              {translate('Confirm Save')}
            </Button>
          </div>
        </div>
      )}

      {isError ? (
        <div css={style.overlay}>
          <span role="alert" data-testid="card-feed-cards-error">
            {translate(
              "We couldn't load the cards for this feed. Please try again.",
            )}
          </span>
        </div>
      ) : isLoading ? (
        <div
          css={style.overlay}
          role="status"
          aria-live="polite"
          data-testid="card-feed-cards-loading"
        >
          {translate('Loading...')}
        </div>
      ) : (
        <>
          {isTruncated && (
            <div
              css={style.truncationNotice}
              data-testid="card-feed-cards-truncation"
            >
              {translate(
                'Showing first 100 cards. Sorting and filtering apply to these only.',
              )}
            </div>
          )}
          <div css={style.gridWrap}>
            <DataGrid<CardFeedCardsView, CardFeedCard>
              height={GRID_HEIGHT}
              rows={visibleCards}
              columns={columns}
              filtersBody={view}
              rowHeight={DataGridRowHeight.Default}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              // Only an active feed can take assignments, so non-active feeds
              // render read-only — no row checkboxes (and the columns drop the
              // row-level assign dropdown).
              checkboxSelection={isActive}
              keepNonExistentRowsSelected
              isRowSelectable={(params): boolean =>
                isActive && params.row.status === 'UNASSIGNED'
              }
              rowSelectionModel={selectionModel}
              onRowSelectionModelChange={handleRowSelectionModelChange}
              disableColumnFilter
              disableRowSelectionOnClick
              slots={{ noRowsOverlay }}
            />
          </div>
        </>
      )}

      <CardSelectionSnackbar
        selectedCount={selectedCards.length}
        onAssign={handleAssignSelected}
        onClose={(): void => setSelectionModel(createEmptySelection())}
      />

      <BaseModal
        isOpen={showDeleteConfirm}
        onClose={handleCloseDeleteConfirm}
        aria-label={translate('Delete Saved View Modal')}
      >
        <div className="flex-column gap-5">
          <div className="font-h2">{translate('Delete Saved View')}</div>
          <div className="font-regular">
            {translate('Are you sure you want to delete ${displayName}?', {
              displayName: deletingViewName,
            })}
          </div>
          {deleteViewError && (
            <div className="font-error-message" role="alert">
              {parseBackendErrors(deleteViewError).topLevelError}
            </div>
          )}
        </div>
        <div className="flex-row justify-end gap-6">
          <Button
            variant="no-shell"
            dropShadow={false}
            onClick={handleCloseDeleteConfirm}
            disabled={isDeletingView}
          >
            {translate('Cancel')}
          </Button>
          <Button loading={isDeletingView} onClick={handleConfirmDeleteView}>
            {translate('Confirm')}
          </Button>
        </div>
      </BaseModal>
    </Box>
  )
}

export default CardFeedCardsTable
